import { Controller } from "@hotwired/stimulus"

// Replaces inline `onload` / `onerror` attributes on `<img>` tags so CSP can
// drop `'unsafe-inline'` from script-src. Apply via `data-controller="img-handler"`
// plus a `data-action` that picks the behavior:
//
//   load action (fade-in once the image has decoded):
//     #showLoaded      removes opacity-0, adds opacity-100
//     #showLoadedAndStopPulse  removes opacity-0 from img + animate-pulse from parent
//
//   error action (fallback when the image fails to load):
//     #removeSelf      drops the broken <img>
//     #removeParent    drops the entire container
//     #greyOutAndRemove  marks parent as a grey placeholder then drops the <img>
//     #hideAndShowNext  hides the img and reveals its next sibling (initials/avatar fallback)
//     #setParentText   replaces the parent's text with `data-img-handler-fallback-text-value`
//     #fallbackSrc     swaps in `data-img-handler-fallback-src-value`, then greys-out on a second failure
export default class extends Controller {
  static values = {
    fallbackText: String,
    fallbackSrc: String,
    fallbackClass: String,
    opacity: String
  }

  // Cached images can finish loading before the bundle parses + Stimulus
  // connects the controller — the original load/error event has already
  // fired and our action listener missed it. Re-fire a synthetic event so
  // the configured action runs.
  connect() {
    if (this.element.tagName !== "IMG" || !this.element.complete) return
    const eventName = this.element.naturalWidth > 0 ? "load" : "error"
    queueMicrotask(() => this.element.dispatchEvent(new Event(eventName)))
  }

  showLoaded() {
    this.element.classList.replace("opacity-0", "opacity-100")
  }

  showLoadedAndStopPulse() {
    this.element.classList.remove("opacity-0")
    this.element.parentElement?.classList.remove("animate-pulse", "bg-gray-100")
  }

  removeSelf() {
    this.element.remove()
  }

  removeParent() {
    this.element.parentElement?.remove()
  }

  greyOutAndRemove() {
    const parent = this.element.parentElement
    if (parent) {
      parent.classList.add("bg-gray-200")
      parent.classList.remove("animate-pulse")
    }
    this.element.remove()
  }

  hideAndShowNext() {
    this.element.style.display = "none"
    const next = this.element.nextElementSibling
    if (next) next.style.display = "flex"
  }

  setParentText() {
    if (this.element.parentElement && this.hasFallbackTextValue) {
      this.element.parentElement.textContent = this.fallbackTextValue
    }
  }

  fallbackSrc() {
    if (this.hasFallbackSrcValue && this.element.src !== this.fallbackSrcValue) {
      this.element.src = this.fallbackSrcValue
    } else {
      this.greyOutAndRemove()
    }
  }

  // For company logos: fade in, then sample pixels off-screen to decide
  // whether the logo has transparency (so the header can pick a background
  // class that doesn't clash with it).
  detectLogoTransparency() {
    this.element.classList.remove("opacity-0")
    try {
      const c = document.createElement("canvas")
      c.width = this.element.naturalWidth
      c.height = this.element.naturalHeight
      const ctx = c.getContext("2d")
      ctx.drawImage(this.element, 0, 0)
      const data = ctx.getImageData(0, 0, c.width, c.height).data
      let transparent = false
      for (let i = 3; i < data.length; i += 4) {
        if (data[i] < 250) { transparent = true; break }
      }
      this.element.classList.add(transparent ? "logo-transparent" : "logo-opaque")
    } catch (_e) {
      this.element.classList.add("logo-opaque")
    }
  }

  // For the header logo fallback: hide the img + display:block its next
  // sibling (the text/initials fallback uses `display:block`, not flex).
  hideAndShowNextBlock() {
    this.element.style.display = "none"
    const next = this.element.nextElementSibling
    if (next) next.style.display = "block"
  }

  // Action Items avatars: the avatar sits absolute-positioned over an
  // initials span. On successful load, fade it in (opacity from
  // data-img-handler-opacity-value, default "1") and hide the initials.
  revealOverInitials() {
    this.element.style.opacity = this.hasOpacityValue ? this.opacityValue : "1"
    const prev = this.element.previousElementSibling
    if (prev) prev.style.display = "none"
  }

  // Action Items avatars: on error, swap the img for a span containing the
  // initials in `data-initials`, styled by `data-img-handler-fallback-class-value`.
  replaceWithInitials() {
    const span = document.createElement("span")
    if (this.hasFallbackClassValue) span.className = this.fallbackClassValue
    span.textContent = this.element.dataset.initials || ""
    this.element.replaceWith(span)
  }
}
